import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { IconButton } from '@dealstack/frontend/shared/components/fundament/IconButton/IconButton';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import styled from 'styled-components';
import { IIconProps } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import { IIconButtonProps } from '@dealstack/frontend/shared/components/fundament/IconButton/IconButton.types';
import { IButtonProps } from './Button.types';

export const ActionButton = styled(Button).attrs<Partial<IButtonProps>>({
	children: 'Actions',
	variant: 'primary',
	iconPlacement: 'left',
	icon: 'menu-thick',
})``;

export const SaveDraftButton = styled((props) => {
	const tCommonUi = useTranslationCommonUi();

	return (
		<Button variant="outlined" iconPlacement="left" icon="save" iconSize="lg" {...props}>
			{tCommonUi('common_buttons.save_draft')}
		</Button>
	);
})``;

export const AddNewProjectButton = styled(Button).attrs<Partial<IButtonProps>>({
	children: 'Add New',
	variant: 'project',
	iconPlacement: 'left',
	icon: 'plus',
})`
	justify-content: flex-start;
`;

export const ViewProjectButton = styled(Button).attrs<Partial<IButtonProps>>({
	children: 'View',
	variant: 'project',
	iconPlacement: 'right',
	icon: 'chevron-right',
	iconSize: 'lg',
})`
	justify-content: space-between;
`;

export const TemplateDownloadButton = styled(Button).attrs<Partial<IButtonProps>>({
	children: 'Template Download',
	variant: 'text',
	iconPlacement: 'left',
	icon: 'download',
	iconSize: 'lg',
})``;

export const DownloadInstructionButton = styled(Button).attrs<Partial<IButtonProps>>({
	children: 'Download instructions',
	variant: 'outlined',
	iconPlacement: 'left',
	icon: 'download',
	iconSize: 'md',
})`
	padding: 0.25rem 0.5rem;
`;

export const UploadFileButton = styled(Button).attrs<Partial<IButtonProps>>({
	children: 'Choose file',
	variant: 'outlined',
	iconPlacement: 'left',
	icon: 'upload',
	iconSize: 'lg',
})``;

export const RefreshButton = styled(
	({ isLoading, ...props }: Partial<IButtonProps> & { isLoading?: boolean }) => {
		const tCommonUi = useTranslationCommonUi();

		return (
			<Button
				variant="outlined"
				iconPlacement="left"
				icon={isLoading ? '' : 'refresh'}
				iconSize="lg"
				{...props}
			>
				{isLoading
					? tCommonUi('common_buttons.refresh.is_loading')
					: tCommonUi('common_buttons.refresh.is_not_loading')}
			</Button>
		);
	}
)`
	.svg {
		--color: ${({ theme }) => theme.v2.color.secondary[800]};
	}

	width: 10em;
`;

export const BackButton = styled((props) => {
	const tCommonUi = useTranslationCommonUi();

	return (
		<Button
			variant="outlined"
			iconPlacement="left"
			icon="chevron-left"
			iconSize="lg"
			{...props}
		>
			{tCommonUi('common_buttons.back')}
		</Button>
	);
})``;
export const ContinueButton = styled(
	({ text, ...props }: Partial<IButtonProps> & { text?: string }) => {
		const tCommonUi = useTranslationCommonUi();

		return (
			<Button
				variant="primary"
				iconPlacement="right"
				icon="chevron-right"
				iconSize="lg"
				{...props}
			>
				{text ? text : tCommonUi('common_buttons.continue')}
			</Button>
		);
	}
)``;
export const CancelButton = styled((props) => {
	const tCommonUi = useTranslationCommonUi();

	return (
		<Button
			variant="outlined"
			iconPlacement="left"
			icon="minus-circle"
			iconSize="lg"
			{...props}
		>
			{tCommonUi('common_buttons.cancel')}
		</Button>
	);
})``;
export const SaveButton = styled(
	({ text, ...props }: Partial<IButtonProps> & { text?: string }) => {
		const tCommonUi = useTranslationCommonUi();

		return (
			<Button variant="primary" iconPlacement="left" icon="save" iconSize="lg" {...props}>
				{text ? text : tCommonUi('common_buttons.save')}
			</Button>
		);
	}
)``;
export const CloseButton = styled((props) => {
	const tCommonUi = useTranslationCommonUi();

	return (
		<Button variant="primary" iconPlacement="left" icon="close" {...props}>
			{tCommonUi('common_buttons.close')}
		</Button>
	);
})``;

export const CheckedButtonSquare = styled(IconButton).attrs<Partial<IIconButtonProps>>({
	variant: 'filled',
	icon: 'checkmark',
})`
	background-color: ${({ theme }) => theme.v2.color.secondary[800]};

	.svg {
		--color: ${({ theme }) => theme.v2.color.white[500]};
	}
`;

export const SaveButtonSquare = styled(IconButton).attrs<Partial<IIconButtonProps>>({
	variant: 'filled',
	icon: 'save',
})`
	background-color: ${({ theme }) => theme.v2.color.secondary[800]};

	.svg {
		--color: ${({ theme }) => theme.v2.color.white[500]};
	}
`;
export const EditButtonSquare = styled(IconButton).attrs<Partial<IIconButtonProps>>({
	variant: 'filled',
	icon: 'edit-pencil',
})``;

export const CloseButtonSquare = styled(IconButton).attrs<Partial<IIconButtonProps>>({
	variant: 'filled',
	icon: 'close',
})`
	background-color: ${({ theme }) => theme.v2.color.error[1000]};

	.svg {
		--color: ${({ theme }) => theme.v2.color.white[500]};
	}
`;

export const DeleteButtonSquare = styled(IconButton).attrs<Partial<IIconButtonProps>>({
	variant: 'filled',
	icon: 'trash-can',
})`
	background-color: ${({ theme }) => theme.v2.color.error[1000]};

	.svg {
		--color: ${({ theme }) => theme.v2.color.white[500]};
	}
`;
export const ThreeDotMenuIconButton = styled(Icon).attrs<Partial<IIconProps>>({
	variant: 'three-dot-menu',
	size: 'lg',
})`
	cursor: pointer;
`;
export const PlusCircleIconButton = styled(Icon).attrs<Partial<IIconProps>>({
	variant: 'add-circle',
	size: 'lg',
})`
	cursor: pointer;
	color: ${({ theme }) => theme.v2.color.secondary[800]};
`;

export const DownloadIconButton = styled(IconButton).attrs<Partial<IIconButtonProps>>({
	icon: 'download',
	variant: 'outlined',
	size: 'md',
})`
	--color: ${({ theme }) => theme.v2.color.secondary[800]};
`;
